import { useTheme } from '@emotion/react'
import { CartItem } from '@open-tender/types'
import { hasGroupsBelowMin, useBuilder, useOrderItem } from '@open-tender/utils'
import { correctRemovalModifierCals } from '../../utils/removalModifierCals'
import {
  addItemToCart,
  openModal,
  selectDisplaySettings,
  selectSelectedAllergenNames,
  selectSelectedTagNames,
  showNotification,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { Button, MenuItemOverlay, MenuItemTagAlert } from 'components'
import { UpsellItemButtons, UpsellItemView } from './UpsellItem.styled'
import UpsellItemCard from './UpsellItemCard'

const UpsellItem = ({
  orderItem,
  addCallback,
}: {
  orderItem: CartItem
  addCallback?: () => void
}) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { image } = theme.menuItems
  const showImage = image.position !== 'HIDDEN'
  const displaySettings = useAppSelector(selectDisplaySettings)
  const {
    calories: showCals = false,
    tags: showTags = false,
    allergens: showAllergens = false,
  } = displaySettings || {}
  const allergenAlerts = useAppSelector(selectSelectedAllergenNames)
  const tagsAlerts = useAppSelector(selectSelectedTagNames)
  const { item: rawBuiltItem } = useBuilder(orderItem)
  const builtItem = correctRemovalModifierCals(rawBuiltItem)
  const {
    name,
    imageUrl,
    displayTags,
    displayAllergens,
    displayPrice,
    displayCals,
    isSoldOut,
    allergenAlert,
    tagAlert,
  } = useOrderItem(
    orderItem,
    null,
    allergenAlerts,
    {},
    showCals,
    showTags,
    showAllergens,
    undefined,
    tagsAlerts
  )
  const { quantity, groups } = builtItem
  const groupsBelowMin = hasGroupsBelowMin(null, groups)
  const isIncomplete = `${quantity}` === '' || quantity === 0 || groupsBelowMin

  const imageOverlay = showImage ? (
    <MenuItemOverlay
      isSoldOut={isSoldOut}
      allergenAlert={allergenAlert ?? undefined}
      tagAlert={tagAlert ?? undefined}
    />
  ) : null

  const view = () => {
    dispatch(
      openModal({
        type: 'item',
        args: {
          focusFirst: true,
          cartItem: builtItem,
          isInUpsell: true,
          addCallback,
        },
      })
    )
  }

  const add = () => {
    if (!isIncomplete) {
      dispatch(addItemToCart(builtItem))
      dispatch(showNotification(`${builtItem.name} added to cart!`))
      addCallback?.()
    }
  }

  return (
    <UpsellItemView>
      <UpsellItemCard
        showImage={showImage}
        imageUrl={imageUrl ?? ''}
        imageOverlay={imageOverlay}
        name={name ?? ''}
        price={displayPrice}
        cals={displayCals ?? ''}
        tags={displayTags}
        allergens={displayAllergens}
      />
      {!showImage ? (
        <MenuItemTagAlert
          isSoldOut={isSoldOut}
          allergenAlert={allergenAlert ?? ''}
        />
      ) : null}
      <UpsellItemButtons>
        <Button onClick={isIncomplete ? view : add} size="small">
          Add To Order
        </Button>
      </UpsellItemButtons>
    </UpsellItemView>
  )
}

export default UpsellItem
